import { palette, spaces } from '@kimuno/theme';
import { Box, Button, Stack, SvgIcon, type SxProps, Typography } from '@mui/material';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateGeneratedDocument as updateGeneratedDocumentAction } from '../../../../actions/aiAssistant.actions';
import { AsyncActionStatus } from '../../../../actions/common.actionTypes';
import type { StoreType } from '../../../../reducers';
import {
	getCurrentConversationId,
	getGeneratedDocumentSync,
	isDocumentLoading as isDocumentLoadingSelector,
} from '../../../../reducers/aiAssistant.selectors';
import services from '../../../../services';
import {
	AIAssistantDisplayMode,
	aiAssistantContext,
} from '../../../../state/AIAssistantContextProvider';
import {
	AIAssistantEditorMode,
	aiAssistantEditorContext,
} from '../../../../state/AIAssistantEditorProvider';
import getMuiIcon from '../../../../utils/getMuiIcon';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';
import useAction from '../../../../utils/useAction';
import AutoSave from '../../../AutoSave/AutoSave';
import InlineEditableTitle from '../../../InlineEditableTitle';
import AIAssistantIconButton from '../../Dialog/AIAssistantIconButton';
import AnimatedEllipsis from '../Chat/Messages/AnimatedEllipsis';
import { DEFAULT_HEADLINE_HEIGHT } from '../constants';
import AIAssistantTextEditorCopyButton from './AIAssistantTextEditorCopyButton';
import AIAssistantTextEditorExportButton, {
	type TextEditorDownloadFormat,
} from './AIAssistantTextEditorExportButton';
import usePodcastAudioDownload from './usePodcastAudioDownload';

const getTopBarStyles = (isPageMode: boolean): SxProps =>
	isPageMode
		? {
				display: 'flex',
				padding: `${spaces.sm} ${spaces.md} ${spaces.sm} ${spaces.xl}`,
				alignItems: 'center',
				gap: spaces.md,
				borderBottom: `1px solid ${palette.elevation.outlined}`,
				background: palette.background.paperElevation2,
			}
		: {
				position: 'relative',
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'space-between',
				alignItems: 'center',
				gap: spaces.xs,
				paddingX: spaces.md,
				paddingY: spaces.sm,
				borderBottom: `1px solid ${palette.primary.contrast}`,
				height: `${DEFAULT_HEADLINE_HEIGHT}px`,
			};

const editableTitleStyles = {
	flex: '1 1 auto',
	minWidth: 0,
	color: palette.text.primary,
};

type Props = {
	// During podcast generation the editor isn't open yet (mode !== PODCAST), but the
	// bar must still show the podcast actions (disabled download) instead of the document ones.
	isPodcastLoading?: boolean;
};

const AIAssistantEditorTopBar = ({ isPodcastLoading = false }: Props) => {
	const { t } = useTranslation();
	const {
		editorData,
		mode,
		closeTextEditor,
		podcastDetails,
		updatePodcastTitle,
		flushPodcastTitleSave,
	} = useContext(aiAssistantEditorContext);
	const { displayMode, generatingPodcast, closePodcastLoadingView } = useContext(aiAssistantContext);
	const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;
	const isPodcast = mode === AIAssistantEditorMode.PODCAST;
	const showPodcastActions = isPodcast || isPodcastLoading;
	const currentConversationId = useSelector(getCurrentConversationId);
	const truncateSx: SxProps = getTextTruncationSx(1);
	const updateGeneratedDocument = useAction(updateGeneratedDocumentAction);
	const syncData = useSelector(getGeneratedDocumentSync);
	const isDocumentLoading = useSelector((state: StoreType) =>
		isDocumentLoadingSelector(state, editorData.id)
	);

	// A rename must save at once, so a document still streaming (its title is
	// still being written) or a podcast whose details haven't loaded stays plain text.
	const isTitleEditable = isPodcast
		? Boolean(podcastDetails?.id)
		: !isPodcastLoading && Boolean(editorData.id) && !isDocumentLoading;

	const savePodcastTitle = (title: string) => {
		updatePodcastTitle(title);
		flushPodcastTitleSave();
	};

	const saveTitle = (title: string) =>
		isPodcast
			? savePodcastTitle(title)
			: updateGeneratedDocument({ documentId: editorData.id ?? '', payload: { title } });

	const feature = [isPodcast ? 'podcast generation' : 'document generation'];

	const onDownload = (format: TextEditorDownloadFormat) => {
		if (!currentConversationId) return;
		services.track.ampliTrackTextEditorFileExportedEvent({
			conversationId: currentConversationId,
			exportType: 'download',
			exportFormat: format,
			feature,
		});
	};

	const onPrint = () => {
		if (!currentConversationId) return;
		services.track.ampliTrackTextEditorFileExportedEvent({
			conversationId: currentConversationId,
			exportType: 'print',
			exportFormat: 'pdf',
			feature,
		});
	};

	const onShare = () => {
		if (!currentConversationId) return;
		services.track.ampliTrackTextEditorFileExportedEvent({
			conversationId: currentConversationId,
			exportType: 'share',
			feature,
		});
	};

	const { handleAudioDownload, isAudioDownloadDisabled } = usePodcastAudioDownload();

	const handlePodcastClose = () => {
		flushPodcastTitleSave();
		closeTextEditor();
	};

	const syncTargetId = isPodcast ? podcastDetails?.id : editorData.id;
	const isSyncing = Boolean(
		syncData.id && syncData.id === syncTargetId && syncData.status === AsyncActionStatus.LOADING
	);

	return (
		<Box data-testid="ai-assistant-text-editor-top-bar" sx={getTopBarStyles(isPageMode)}>
			<Stack direction="row" gap={spaces.xs} alignItems="center" flex={1} minWidth={0}>
				{isTitleEditable ? (
					<InlineEditableTitle
						variant="h6"
						sx={editableTitleStyles}
						value={editorData.title}
						onSave={saveTitle}
						inputTestId="ai-assistant-text-editor-title-input"
					/>
				) : (
					<Typography
						data-testid="ai-assistant-text-editor-title-text"
						variant="h6"
						sx={{ ...truncateSx, color: palette.text.primary }}
					>
						{isPodcastLoading && !editorData.title ? (
							generatingPodcast?.title ? (
								generatingPodcast.title
							) : (
								<>
									{t('Podcast is being created')}
									<AnimatedEllipsis />
								</>
							)
						) : (
							editorData.title
						)}
					</Typography>
				)}
			</Stack>
			{showPodcastActions ? (
				<Stack direction="row" gap={spaces.sm} alignItems="center">
					<AutoSave status={isSyncing ? 'saving' : 'saved'} />
					<Button
						data-testid="podcast-audio-download-button"
						variant="contained"
						size="small"
						disabled={isAudioDownloadDisabled}
						onClick={handleAudioDownload}
						startIcon={<SvgIcon component={getMuiIcon('download')} aria-hidden="true" />}
					>
						{t('Audio herunterladen')}
					</Button>
					{/* While loading, the pane is driven by activeView — closing flips it back to CHAT
              and the generation continues via the chat card + background poll. */}
					{(isPodcast || isPodcastLoading) && (
						<AIAssistantIconButton
							data-testid="text-editor-close-button"
							icon="close"
							tooltip={t('Close')}
							onClick={isPodcastLoading ? closePodcastLoadingView : handlePodcastClose}
						/>
					)}
				</Stack>
			) : (
				<Stack direction="row" gap={spaces.xxs} alignItems="center">
					{editorData.id && <AutoSave status={isSyncing ? 'saving' : 'saved'} />}
					<AIAssistantTextEditorCopyButton />
					<AIAssistantTextEditorExportButton
						id="ai-editor-export-button"
						onPrint={onPrint}
						onDownload={onDownload}
						onShare={onShare}
					/>
					<AIAssistantIconButton
						data-testid="text-editor-close-button"
						icon="close"
						tooltip={t('Close')}
						onClick={closeTextEditor}
					/>
				</Stack>
			)}
		</Box>
	);
};

export default AIAssistantEditorTopBar;
